import { useState, useCallback, useEffect } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import Title from '@zenbusiness/common-components/Title';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';

import ProductModal from '#client/components/Modals/ProductModal';
import RovieModal from '#client/components/RovieModal';
import { MODAL_PARAM_KEY, PRODUCT_MODALS } from '#client/pages/Home/constants';
import { removeUrlParam, appendUrlParam } from '#client/pages/Home/utils';
import { generateEntitlements } from '#client/utils/entitlements';
import getPricingDeviations from '#client/utils/pricing';

import { ExpertHelp } from './ExpertHelp';
import LogoBuilderBeta from './LogoBuilderBeta';
import MeetWithExperts from './MeetWithExperts';
import Products from './Products';
import QuickActions from './QuickActions';
import Scheduling from './Scheduling';
import Website from './Website';

import EditorsModal from '../WebsiteList/components/EditorsModal/EditorsModal';
import {
  WEBSITE_PRODUCTS,
  LD_ENABLE_SCHEDULING_SECTION,
  LD_ENABLE_EXPERT_HELP_SECTION,
  LOGO_TR_TEST_MAX_USERS,
  LD_ENABLE_LB_TEST,
  MT_DATA_TRACKING,
  LD_RELEASE_TRY_BEFORE_YOU_BUY,
  websiteEditorModalParam,
  MARKETING_TOOLS_MODAL_PARAM_KEY
} from './utils/constants';
import useGetLogoUsersQuery from './utils/hooks/useGetLogoUsersQuery';
import { MARKETING_TOOLS_HOME_QUERY } from './utils/queries';

// TODO: remove schedule section and references after the new Expert help section is completed
const MarketingTools = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { businessEntityUuid } = useBusinessEntity();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState('');
  const releaseTryBeforeYouBuy = useExperimentFlag(LD_RELEASE_TRY_BEFORE_YOU_BUY, false);

  const [isWebsiteDudaModalOpen, setIsWebsiteDudaModalOpen] = useState(false);
  const {
    data: {
      accountInfo,
      businessEntity,
      orderTasksLegacy: { orderTasksLegacy = [] } = {},
      vanityPricing,
      websites: { websites = [] } = {}
    } = {},
    loading
  } = useQuery(MARKETING_TOOLS_HOME_QUERY, {
    variables: { businessEntityUuid }
  });

  // The user could have gotten the website without purchasing it,
  // through the "Try before you buy" offer.
  const hasActiveWebsites = websites.some((website) => !website.deleted && website.active);

  // TODO: Migrate to the new service to fetch entitlements, https://counsl.atlassian.net/browse/WEB-1807
  const entitlements = generateEntitlements(orderTasksLegacy);
  // Show quick actions section if any website products have been already purchased
  // REF: https://counsl.atlassian.net/wiki/spaces/GC/pages/3669622792/Requirements+of+Website+Marketing+Home+Page+MVE
  const hasPurchasedWebToolsProducts = Object.values(WEBSITE_PRODUCTS).some(
    (product) => entitlements.has(product)
  );
  const showQuickActionsSection = hasPurchasedWebToolsProducts || hasActiveWebsites;

  const hasPurchasedWebsite = entitlements.has(WEBSITE_PRODUCTS.WEBSITE);

  // Hide website section if website product have been already purchased
  // or if user has already a website through TBUB offer.
  // REF: https://counsl.atlassian.net/wiki/spaces/GC/pages/3669622792/Requirements+of+Website+Marketing+Home+Page+MVE
  const showWebsiteSection = !hasPurchasedWebsite && !hasActiveWebsites;
  const showScheduleSection = useExperimentFlag(LD_ENABLE_SCHEDULING_SECTION, false);
  const showExpertHelpSection = useExperimentFlag(LD_ENABLE_EXPERT_HELP_SECTION, false);
  const showLBBetaInvitation = useExperimentFlag(LD_ENABLE_LB_TEST, false);
  const logoTRTestMaxUsers = useExperimentFlag(LOGO_TR_TEST_MAX_USERS, 250);

  useEffect(() => {
    const isWebsiteEditorModalOpen = searchParams.get(MARKETING_TOOLS_MODAL_PARAM_KEY);
    if (isWebsiteEditorModalOpen !== websiteEditorModalParam) return;

    setIsWebsiteDudaModalOpen(true);
  }, [searchParams]);

  const { isLoading, logoUsersCount } = useGetLogoUsersQuery();

  const handleOpenModal = useCallback(
    (modal) => {
      if (modal === websiteEditorModalParam) return;
      setCurrentModal(modal);
      setIsModalOpen(true);
      appendUrlParam({
        navigate,
        location,
        paramName: modal,
        paramKey: MODAL_PARAM_KEY
      });
    },
    [navigate, location]
  );

  useEffect(() => {
    const modalName = searchParams.get(MODAL_PARAM_KEY);
    if (!modalName || isModalOpen) return;
    handleOpenModal(modalName);
  }, [handleOpenModal, searchParams, isModalOpen]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    removeUrlParam({ navigate, location, paramKey: MODAL_PARAM_KEY });
  }, [navigate, location]);

  const handleLearnMoreClick = (product) => () => {
    handleOpenModal(product);
  };

  if (loading) {
    return null;
  }

  const pricingDeviations = getPricingDeviations([...accountInfo.pricing, ...vanityPricing]);

  const isLogoTRTestActive = (() => {
    if (!isLoading) {
      return logoUsersCount < logoTRTestMaxUsers;
    }
    return false;
  })();

  return (
    <Box
      pb={{ xs: 2, md: 4 }}
      data-testid="marketing-tools-page"
    >
      <Box
        pt={{ xs: 2, sm: 6 }}
        mb={{ xs: 4, sm: 5 }}
        sx={{
          px: 2,
          mx: -2
        }}
      >
        <Title level={1} variant="md-alt" mb={{ xs: 2, md: 3 }}>
          Website & Marketing
        </Title>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={{ xs: 8, md: 10 }}
      >
        {showQuickActionsSection && (
          <QuickActions entitlements={entitlements} showWebsiteAction={!showWebsiteSection} />
        )}
        {showWebsiteSection && (
          <Website onLearnMoreClick={handleLearnMoreClick(PRODUCT_MODALS.WEBSITE_DUDA)} />
        )}
        <Products
          entitlements={entitlements}
          hasActiveWebsites={hasActiveWebsites}
          onLearnMoreClick={handleLearnMoreClick}
        />
        {showScheduleSection && <Scheduling />}
        {showExpertHelpSection && <ExpertHelp />}
        <MeetWithExperts />
      </Box>
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'fixed',
          bottom: '16px',
          left: { md: 150, lg: 170 },
          right: { md: 55, lg: 75 }
        }}
      >
        <LogoBuilderBeta
          showLBBanner={showLBBetaInvitation}
          isTRTestActive={isLogoTRTestActive}
          businessEntityUuid={businessEntityUuid}
        />
      </Box>
      <RovieModal open={isModalOpen} onClose={handleCloseModal}>
        <ProductModal
          account={accountInfo}
          businessEntity={businessEntity}
          entitlements={entitlements}
          handleOpenModal={handleOpenModal}
          modalName={currentModal}
          modalRef={(node) => node?.scrollIntoView({ behavior: 'smooth' })}
          pricingDeviations={pricingDeviations}
          buttonDataTrackingProps={{
            ...MT_DATA_TRACKING
          }}
          modalDataTrackingProps={{
            ...MT_DATA_TRACKING,
            ...(releaseTryBeforeYouBuy && { experimentName: 'try_before_you_buy' })
          }}
        />
      </RovieModal>
      <EditorsModal
        isOpen={isWebsiteDudaModalOpen}
        hasActiveWebsite={hasActiveWebsites}
        setIsOpen={setIsWebsiteDudaModalOpen}
        dataTrackingProps={MT_DATA_TRACKING}
      />
    </Box>
  );
};

export default MarketingTools;
